import { apiService, API_ENDPOINTS } from '@/shared/services/api';
import type { APIResponse } from '@/shared/types';
import type { SalesReport, PayReport } from '../types';
import type { TrainingMaterial, ComplianceMaterial } from '@/features/campaigns';

export class AgentService {
  static async createClientEnrollment(data: {
    clientName: string;
    campaignId: string;
    amount: number;
    reportDate: string;
  }): Promise<SalesReport> {
    const response = await apiService.post<APIResponse<SalesReport>>(
      API_ENDPOINTS.agent.clientEnrollment,
      data
    );
    return response.data.data!;
  }

  static async getSalesReports(filters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
  }): Promise<SalesReport[]> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);

    const response = await apiService.get<APIResponse<SalesReport[]>>(
      `${API_ENDPOINTS.agent.salesReports}?${params.toString()}`
    );
    return response.data.data!;
  }

  static async getPayReports(filters?: {
    payPeriodStart?: string;
    payPeriodEnd?: string;
    status?: string;
  }): Promise<PayReport[]> {
    const params = new URLSearchParams();
    if (filters?.payPeriodStart) params.append('payPeriodStart', filters.payPeriodStart);
    if (filters?.payPeriodEnd) params.append('payPeriodEnd', filters.payPeriodEnd);
    if (filters?.status) params.append('status', filters.status);

    const response = await apiService.get<APIResponse<PayReport[]>>(
      `${API_ENDPOINTS.agent.payReports}?${params.toString()}`
    );
    return response.data.data!;
  }

  static async getTrainingMaterials(): Promise<TrainingMaterial[]> {
    const response = await apiService.get<APIResponse<TrainingMaterial[]>>(
      API_ENDPOINTS.agent.trainingMaterials
    );
    return response.data.data!;
  }

  static async getComplianceMaterials(): Promise<ComplianceMaterial[]> {
    const response = await apiService.get<APIResponse<ComplianceMaterial[]>>(
      API_ENDPOINTS.agent.complianceMaterials
    );
    return response.data.data!;
  }

  static async shareInstructions(data: {
    customerEmail: string;
    instructionsContent: string;
  }): Promise<void> {
    await apiService.post<APIResponse>(
      API_ENDPOINTS.agent.shareInstructions,
      data
    );
  }

  static async sharePdfResources(data: {
    customerEmail: string;
    customerName: string;
    pdfResources: Array<{
      id: string;
      name: string;
      description: string;
      filename: string;
      category: string;
    }>;
    emailSubject: string;
  }): Promise<void> {
    await apiService.post<APIResponse>(
      API_ENDPOINTS.agent.shareInstructions, // Reuse the same endpoint for now
      {
        customerEmail: data.customerEmail,
        instructionsContent: `PDF Resources: ${data.pdfResources.map(pdf => pdf.name).join(', ')}`
      }
    );
  }
}
